/** The caller identity (`GET /me`) and the features this API serves. */
import { z } from 'zod';
import { OrgRole, ProjectRole, Scope, uuid } from './common.js';

/**
 * `Me.features` (api/openapi.yaml `MeFeatures`): what this Hangar version really serves, so a client hides or
 * disables an action instead of sending it to a route that answers `501 HGR-NOT-IMPLEMENTED`. A flag missing from an
 * older API reads as `false`. The caller's scopes still apply on top (`Me.scopes`). The names are the ones the
 * console reads (apps/console/src/lib/permissions.ts).
 */
export const MeFeatures = z.object({
  /** Provider tokens (`eas_access_token`, `bitrise_api_token`) through `/credentials`: create, list, rotate, revoke. */
  credentials: z.boolean().default(false),
  /** Console *Run build*: the launch form (`GET /profiles/{profileId}/launch-form`) and a runner that starts the build. */
  runBuild: z.boolean().default(false),
  /** `POST /builds/{buildId}/retry`. */
  retry: z.boolean().default(false),
  /** `POST /apps/{appId}/config/sync` (hangar.yml → profiles). */
  configSync: z.boolean().default(false),
  /** `hangar_runner` execution: Hangar starts the provider build itself. */
  runner: z.boolean().default(false),
  /** Bitrise provider accounts can build (the worker has a Bitrise adapter). */
  bitrise: z.boolean().default(false),
  /** Members and invitations (`/members`, `/invitations`, `/projects/{projectId}/members`). */
  members: z.boolean().default(false),
  /** Client orgs for platform operators (`GET/POST /platform/orgs`); the caller still needs `user.platformRole`. */
  clients: z.boolean().default(false),
  /** Create and edit environments (`POST /projects/{projectId}/environments`, `PATCH /environments/{environmentId}`). */
  environmentsEdit: z.boolean().default(false),
  /** Install page of an artifact (`GET /artifacts/{artifactId}/install`). */
  installPage: z.boolean().default(false),
  /** Artifact listing and download (`GET /builds/{buildId}/artifacts`, `GET /artifacts/{artifactId}/download`). */
  artifacts: z.boolean().default(false),
  /** Personal and service API tokens (`/tokens`). */
  apiTokens: z.boolean().default(false),
  /** CI trust policies and the OIDC exchange (`/oidc-policies`, `POST /auth/oidc/token`). */
  oidcTrust: z.boolean().default(false),
  /** The queue view (`GET /queue`). */
  queue: z.boolean().default(false),
  /** `GET /audit-logs`. */
  auditLog: z.boolean().default(false),
  /** Usage, plan and limits of the org (`GET /usage`, `GET /usage-limits`, `GET /usage/export`). */
  usage: z.boolean().default(false),
  /** Source control and "Create on Bitrise" (`/scm-connections`, `/apps/{appId}/bitrise`). */
  sourceControl: z.boolean().default(false),
  /** Apple signing material through `/credentials` (certificate, profile, App Store Connect key), custodied by platform operators. */
  appleSigning: z.boolean().default(false),
});
export type MeFeatures = z.infer<typeof MeFeatures>;

export const Me = z.object({
  actor: z.object({ type: z.enum(['user', 'api_token', 'oidc']), id: z.string(), label: z.string() }),
  /** `platformRole`: `operator` for Malible staff who custody Apple signing material (decision of 2026-10-22); absent from an older API. */
  user: z.object({ id: uuid, email: z.string(), name: z.string(), isExternal: z.boolean(), platformRole: z.enum(['operator']).nullable().optional() }).nullable(),
  org: z.object({ id: uuid, slug: z.string(), name: z.string() }),
  orgRole: OrgRole.nullable(),
  /** User sessions: every org the user may act in (switch with `X-Hangar-Org`); absent for tokens and from an older API. */
  orgs: z.array(z.object({ id: uuid, slug: z.string(), name: z.string(), role: OrgRole })).optional(),
  /** User sessions: open invitations to orgs the user is not in yet (the org switcher joins one); absent from an older API. */
  pendingInvitations: z.array(z.object({ id: uuid, org: z.object({ id: uuid, slug: z.string(), name: z.string() }), orgRole: OrgRole, expiresAt: z.string() })).optional(),
  /** True while a platform operator previews this client org read-only (`X-Hangar-Preview-Org`). */
  preview: z.boolean().optional(),
  membershipExpiresAt: z.string().nullable(),
  projects: z.array(z.object({ projectId: uuid, slug: z.string(), role: ProjectRole, expiresAt: z.string().nullable() })),
  scopes: z.array(Scope),
  tokenExpiresAt: z.string().nullable(),
  /** Absent from an API that predates it (and from the console's mock): treat every feature as unknown. */
  features: MeFeatures.optional(),
});
export type Me = z.infer<typeof Me>;
